import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, getArticleBySlug } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { comments } from '@/mocks/comments';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const category = article ? categories.find((c) => c.slug === article.category) : null;
  const articleComments = comments.filter((c) => c.articleId === article?.id);
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">文章不存在</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <article className="pt-20 pb-10 px-4 md:px-6">
          <div className="mx-auto max-w-4xl">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              {category && (
                <>
                  <Link
                    to={`/category/${category.slug}`}
                    className="hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    {category.name}
                  </Link>
                  <i className="ri-arrow-right-s-line"></i>
                </>
              )}
              <span className="text-foreground-700 line-clamp-1">{article.title}</span>
            </nav>

            <div className="mb-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                {category?.name || article.category}
              </span>
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {(article.viewCount / 10000).toFixed(1)}万阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  {article.commentCount}评论
                </span>
              </div>
            </div>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-auto object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed text-base article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-secondary-100 hover:text-secondary-700 transition-colors"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 p-6 bg-background-100 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-foreground-900 mb-1">觉得这篇爆料不错？</p>
                <p className="text-xs text-foreground-500">分享给你的吃瓜小伙伴一起讨论吧</p>
              </div>
              <div className="flex items-center gap-2">
                <button className="w-9 h-9 rounded-lg bg-background-50 flex items-center justify-center text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer" aria-label="点赞">
                  <i className="ri-thumb-up-line"></i>
                </button>
                <button className="w-9 h-9 rounded-lg bg-background-50 flex items-center justify-center text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer" aria-label="收藏">
                  <i className="ri-bookmark-line"></i>
                </button>
                <button className="w-9 h-9 rounded-lg bg-background-50 flex items-center justify-center text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享">
                  <i className="ri-share-forward-line"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-20 px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-8">
                相关爆料推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer flex flex-col"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {related.isHot && (
                        <div className="absolute top-3 left-3">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-500/90 text-white text-xs font-medium">
                            <i className="ri-fire-line text-[10px]"></i>热
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold font-heading text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {related.title}
                      </h3>
                      <p className="mt-2 text-xs text-foreground-500">{related.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="bg-background-50 py-14 md:py-20 px-4 md:px-6">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
              吃瓜评论
            </h2>
            <p className="text-sm text-foreground-500 mb-8">
              共 {articleComments.length} 条评论，快来参与讨论吧
            </p>

            <div className="mb-10 p-5 bg-background-100 rounded-xl">
              <textarea
                placeholder="写下你的吃瓜感想……"
                rows={3}
                maxLength={500}
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none resize-none mb-3"
              ></textarea>
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-400">最多500字</span>
                <button className="whitespace-nowrap px-5 py-2 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer">
                  发布评论
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-5">
              {articleComments.map((comment) => (
                <div key={comment.id} className="bg-background-100 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={comment.avatar} alt={comment.user} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-foreground-900">{comment.user}</span>
                        <span className="text-xs text-foreground-400">{comment.date}</span>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
                      <div className="mt-3 flex items-center gap-4 text-xs text-foreground-400">
                        <button className="flex items-center gap-1 hover:text-primary-500 transition-colors cursor-pointer">
                          <i className="ri-thumb-up-line"></i>
                          {comment.likes > 1000 ? `${(comment.likes / 1000).toFixed(1)}k` : comment.likes}
                        </button>
                        <button className="flex items-center gap-1 hover:text-primary-500 transition-colors cursor-pointer">
                          <i className="ri-chat-1-line"></i>
                          回复
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            datePublished: article.publishDate,
            dateModified: article.publishDate,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: '91爆料网',
              url: 'http://cyhkjo.cn/',
            },
            url: `http://cyhkjo.cn/article/${article.slug}`,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `http://cyhkjo.cn/article/${article.slug}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'http://cyhkjo.cn/',
              },
              ...(category ? [{
                '@type': 'ListItem',
                position: 2,
                name: category.name,
                item: `http://cyhkjo.cn/category/${category.slug}`,
              }] : []),
              {
                '@type': 'ListItem',
                position: category ? 3 : 2,
                name: article.title,
              },
            ],
          }),
        }}
      />

      <style>{`
        .article-content p {
          margin-bottom: 1.25rem;
          line-height: 1.85;
          font-size: 1rem;
        }
        .article-content p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </>
  );
}